import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/styles";
import { connect } from "react-redux";
import { I18n } from "@aws-amplify/core";
import { get, map, size } from "lodash";

import { fetchPost } from "../../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import TableRow from "@material-ui/core/TableRow";

import TableCellStyled from "../../Common/TableCellStyled";
import ScrollableTable from "../../Common/Table/ScrollableTable";
import TemperatureGraph from "../Temperature/TemperatureGraph";
import TemperatureMetricsNew from "../Temperature/TemperatureMetricsNew";
import EventsDialog from "../../Events/EventsDialog";

import { getLov } from "../../../utils/lov";
import { formatDate, toUTCTimestamp } from "../../../utils/date";
import { isUnknownProfile } from "../../../utils/profile";
import {
  getTemperatureByOperation,
  getThreshold,
} from "../../../utils/temperature";

import { LANGUAGES } from "../../../constants/common";

import images from "../../../assets/img/images";
import colors from "../../../assets/style/colors";
import FoldableSection from "../../Common/FoldableSection";

const styles = {
  title: {
    fontWeight: 500,
    marginBottom: "10px",
    display: "flex",
    alignItems: "center",
  },
  chartContainer: {
    padding: "0px 24px",
  },
  tableRowHover: {
    "&:hover": {
      backgroundColor: colors.lightGreenBg + " !important",
    },
  },
};

const columns = [
  {
    name: "site_temperatures",
    minWidth: 100,
  },
  {
    name: "users_dateTime",
    minWidth: 200,
  },
  {
    name: "profiles_siteZone",
    minWidth: 150,
  },
  {
    name: "stat_eventGroupId",
    minWidth: 100,
  },
];

const TemperatureDetails = (props) => {
  const {
    classes,
    profileId,
    currentTemperature,
    disableCurrent,
    language,
    refresh,
  } = props;
  const [lov, setLov] = React.useState({});
  const [activeItem, setActiveItem] = React.useState();
  const [activeItemId, setActiveItemId] = React.useState();
  const [tempHistory, setTempHistory] = React.useState([]);
  const [historySize, setHistorySize] = React.useState();
  const [isFetching, setIsFetching] = React.useState(false);

  React.useEffect(() => {
    const fetchLov = async () => {
      setLov({
        88: await getLov(88),
      });
    };
    fetchLov();
    setActiveItemId();
    setActiveItem();
    setTempHistory([]);
    setHistorySize();
    if (currentTemperature) {
      setActiveItemId({
        id: get(currentTemperature, ["id"]),
        parent: { id: get(currentTemperature, ["parent", "id"]) },
      });
      setActiveItem(currentTemperature);
    }
    if (currentTemperature || !isUnknownProfile(profileId)) {
      fetchProfileTempHistory(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, currentTemperature]);

  // Fetch item on activeItem change
  React.useEffect(() => {
    if (activeItemId) {
      fetchItem(activeItemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItemId]);

  const fetchProfileTempHistory = async (refresh) => {
    try {
      if (!isFetching) {
        setIsFetching(true);
        let res = await fetchPost("new/event_groups", {
          pagination: {
            limit: isUnknownProfile(profileId) ? 1 : 6,
            offset: size(tempHistory) > 0 && !refresh ? size(tempHistory) : 0,
          },
          filter: {
            types: [13],
            profile_id: profileId,
            created_end:
              toUTCTimestamp(get(currentTemperature, ["created_on"])) + 2000,
          },
          data: [
            "id",
            "active",
            "created_on",
            "created_by",
            "type",
            "profile",
            "event_count",
            "parent",
            "start",
            "end",
            "parent",
            {
              name: "events",
              data: [
                "id",
                "active",
                "created_on",
                "created_by",
                "type",
                "zone",
                "event_count",
                "attributes",
              ],
            },
          ],
        });
        if (refresh) {
          setTempHistory(res.items);
        } else {
          setTempHistory([...tempHistory, ...res.items]);
        }
        setHistorySize(res.total);
        setIsFetching(false);
      }
    } catch (e) {
      console.log(e);
      setIsFetching(false);
    }
  };

  // Fetch item with images and parent for the start time
  const fetchItem = async (item) => {
    try {
      let res = await fetchPost("new/event_groups", {
        filter: {
          ids: [get(item, ["id"])],
        },
        data: [
          "id",
          "active",
          "created_on",
          "created_by",
          "type",
          "profile",
          "event_count",
          "parent",
          "start",
          "end",
          {
            name: "events",
            data: [
              "id",
              "active",
              "created_on",
              "created_by",
              "type",
              "zone",
              "event_count",
              "attributes",
              "images",
            ],
          },
        ],
      });
      let parent = await fetchPost("new/event_groups", {
        filter: {
          ids: [get(item, ["parent", "id"])],
        },
        data: ["id", "start"],
      });
      setActiveItem({
        ...get(res, ["items", 0]),
        id: get(activeItem, ["id"]),
        parentStart: get(parent, ["items", 0, "start"]),
      });
    } catch (e) {
      console.log(e);
    }
  };

  const renderTempRow = (item) => {
    const maxTemp = getTemperatureByOperation(item, "max");
    const threshold = getThreshold(item);

    return (
      <>
        <TableCellStyled
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={
              maxTemp > threshold
                ? images.temperatureButtonRed
                : images.temperatureButtonGreen
            }
            alt="red"
            height="20px"
            style={{ display: "inline-block" }}
          />
        </TableCellStyled>
        <TableCellStyled className="text-54">
          {get(currentTemperature, ["id"]) === get(item, ["id"]) && (
            <Box style={{ fontSize: "0.750em" }}>
              {I18n.get("site_currentEventLog")}
            </Box>
          )}
          {formatDate(get(item, ["created_on"]))}
        </TableCellStyled>
        <TableCellStyled className="text-54">
          {`${get(item, ["events", 0, "zone", "site", "name"])}/${get(item, [
            "events",
            0,
            "zone",
            "name",
          ])}`}
        </TableCellStyled>
        <TableCellStyled>
          <EventsDialog
            eventGroupId={get(item, ["id"])}
            text={get(item, ["id"])}
            disabled={props.blockLevel2}
          />
        </TableCellStyled>
      </>
    );
  };

  return (
    <Grid container direction="column">
      <Grid item style={{ margin: "24px 0px" }}>
        <Grid container wrap="nowrap">
          {!props.logDetailsDisplay && (
            <Grid
              item
              xs
              style={{ maxWidth: "800px", padding: "0px 60px 0px 24px" }}
            >
              <Grid container direction="column">
                <Grid item className={classes.title}>
                  <img
                    src={images.tabHistoryBlack}
                    alt="history"
                    height="16px"
                  />
                  <span>
                    {I18n.get("site_profileTemperatureHistory").toUpperCase()}
                  </span>
                </Grid>
                <Grid item style={{ marginBottom: "3px" }}>
                  <Grid container wrap="nowrap">
                    <Grid item xs className="text-54 text-italic">
                      {I18n.get("site_selectRowToViewDetails")}
                    </Grid>
                    {/* <Grid
                    item
                    className={theme.link}
                    onClick={() => {
                      if (currentTemperature) {
                        setActiveItemId({
                          id: get(currentTemperature, ["id"]),
                          parent: {
                            id: get(currentTemperature, ["parent", "id"]),
                          },
                        });
                      }
                    }}
                  >
                    {I18n.get("site_viewCurrent")}
                  </Grid> */}
                  </Grid>
                </Grid>
                <Grid item>
                  <ScrollableTable
                    columns={columns}
                    hasMore={size(tempHistory) < historySize}
                    fetchData={fetchProfileTempHistory}
                    isFetching={isFetching}
                    stickyHeader
                    lazyLoading={
                      !profileId || isUnknownProfile(profileId) ? false : true
                    }
                    containerHeight={373}
                  >
                    {!currentTemperature && !disableCurrent && (
                      <TableRow
                        hover
                        classes={{ hover: classes.tableRowHover }}
                        onClick={() => {
                          setActiveItem();
                          setActiveItemId();
                        }}
                        style={{
                          cursor: "pointer",
                          backgroundColor: !get(activeItemId, ["id"])
                            ? colors.activeYellow
                            : colors.white,
                        }}
                      >
                        <TableCellStyled
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={images.temperatureButtonDisabled}
                            alt="grey"
                            height="20px"
                            style={{ display: "inline-block" }}
                          />
                        </TableCellStyled>
                        <TableCellStyled className="text-54">
                          {I18n.get("site_currentEventLog")}
                        </TableCellStyled>
                        <TableCellStyled />
                        <TableCellStyled />
                      </TableRow>
                    )}
                    {map(tempHistory, (item, index) => (
                      <TableRow
                        key={index}
                        onClick={() => {
                          setActiveItemId({
                            id: get(item, ["id"]),
                            parent: { id: get(item, ["parent", "id"]) },
                          });
                        }}
                        hover
                        classes={{ hover: classes.tableRowHover }}
                        style={{
                          cursor: "pointer",
                          backgroundColor:
                            get(activeItemId, ["id"]) === get(item, ["id"])
                              ? colors.activeYellow
                              : index % 2 === 1
                              ? colors.white
                              : colors.tableDarkBg,
                        }}
                      >
                        {renderTempRow(item)}
                      </TableRow>
                    ))}
                  </ScrollableTable>
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item className={classes.chartContainer}>
            <Grid container direction="column">
              <Grid item className={classes.title}>
                {props.logDetailsDisplay ? (
                  <span style={{ marginLeft: 0 }}>
                    {I18n.get("site_capturedTemperatures").toUpperCase()}
                  </span>
                ) : (
                  <>
                    <img
                      src={images.temperatureIcon}
                      alt="thermometer"
                      style={{ marginRight: "10px" }}
                    />
                    <span>
                      {I18n.get("site_temperatureReadings").toUpperCase()}
                    </span>
                  </>
                )}
              </Grid>
              {!props.logDetailsDisplay && (
                <Grid item>
                  <Grid container style={{ height: "23px" }}>
                    <Grid item xs={6} className="text-54">
                      {get(activeItem, ["created_on"]) &&
                        formatDate(get(activeItem, ["created_on"]))}
                    </Grid>
                    <Grid item xs={6} style={{ textAlign: "right" }}>
                      {get(props, ["powerUserMode"]) &&
                        get(activeItem, ["id"]) && (
                          <EventsDialog
                            eventGroupId={get(activeItem, ["id"])}
                            text={`#${get(activeItem, ["id"])}`}
                            disabled={props.blockLevel2}
                          />
                        )}
                    </Grid>
                  </Grid>
                </Grid>
              )}
              <Grid
                item
                style={{
                  border: `1px solid ${colors.veryLighPink}`,
                  borderRadius: "4px",
                  padding: "12px 24px 12px 12px",
                  width: "600px",
                  height: "350px",
                }}
              >
                <Grid container direction="column" style={{ height: "100%" }}>
                  <TemperatureGraph
                    data={activeItem}
                    startTime={get(activeItem, ["parentStart"])}
                    lov={lov}
                    disableCurrent={disableCurrent}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <FoldableSection
        title={I18n.get("site_temperatureReadingsDetails")}
        style={{ paddingLeft: "24px", marginBottom: "-15px", zIndex: 1 }}
      >
        <TemperatureMetricsNew
          data={activeItem}
          language={language}
          lov={lov}
          startTime={get(activeItem, ["parentStart"])}
          maxWidth={props.maxWidth}
          refresh={refresh}
          //blockLevel2={props.blockLevel2}
        />
      </FoldableSection>
    </Grid>
  );
};

TemperatureDetails.propTypes = {
  language: PropTypes.oneOf(LANGUAGES),
};

const mapStateToProps = (state) => {
  return {
    powerUserMode: state.common.powerUserModeEnabled,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(TemperatureDetails));
